/**
 * SelectField — a thin wrapper over SearchableSelect for the editor's simple
 * dropdowns (font, alignment, aspect-ratio, group-by, …). Takes a flat
 * { value, label }[] (optionally with a per-option style, e.g. font preview) and
 * renders the same custom popover the Integration/Metric pickers use, instead of
 * the native <select>. Search is hidden for short lists by default.
 */

import { SearchableSelect } from "./SearchableSelect";

export interface SelectFieldOption {
  value: string;
  label: string;
  /** Optional inline style for the row + trigger (e.g. { fontFamily }). */
  style?: React.CSSProperties;
}

export interface SelectFieldProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectFieldOption[];
  ariaLabel: string;
  testId?: string;
  searchTestId?: string;
  triggerClassName?: string;
  placeholder?: string;
  /** Force-show the search box (default: shown only when > 8 options). */
  searchable?: boolean;
}

export function SelectField({
  value,
  onChange,
  options,
  ariaLabel,
  testId,
  searchTestId,
  triggerClassName,
  placeholder,
  searchable,
}: SelectFieldProps) {
  const showSearch = searchable ?? options.length > 8;
  return (
    <SearchableSelect
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      ariaLabel={ariaLabel}
      hideSearch={!showSearch}
      testId={testId}
      searchTestId={searchTestId}
      triggerClassName={triggerClassName}
      groups={[
        {
          key: "opts",
          label: "",
          options: options.map((o) => ({
            value: o.value,
            searchText: o.label,
            // No aria-label here — the visible label IS the option's accessible
            // name (an inner aria-label would shadow it for getByRole queries).
            content: <span style={o.style}>{o.label}</span>,
          })),
        },
      ]}
    />
  );
}
